import React, { useEffect, useState } from "react";
import privateApi from "../../apis/privateApi";
import "./Report.css";
import Spinner from "../../components/Spinner/Spinner";
import ReportLayout from "../../components/ReportLayout/ReportLayout";
import ReportHeader from "../../components/ReportHeader/ReportHeader";
import SummaryCards from "../../components/SummaryCards/SummaryCards";
import GeographicBreakdown from "../../components/GeographicBreakdown/GeographicBreakdown";
import BudgetLineTable from "../../components/BudgetLineTable/BudgetLineTable";
import ExceptionReports from "../../components/ExceptionReports/ExceptionReports";
import AuditLogs from "../../components/AuditLogs/AuditLogs";
import ChartsSection from "../../components/ChartsSection/ChartsSection";
import AdminUnitBreakdown from "../../components/AdminUnitBreakdown/AdminUnitBreakdown";
import EconomicSegmentBreakdown from "../../components/EconomicSegmentBreakdown/EconomicSegmentBreakdown";
import ProgrammeBreakdown from "../../components/ProgrammeBreakdown/ProgrammeBreakdown";

const Report = () => {
    // ================= STATE =================
    const [summary, setSummary] = useState({});
    const [budgetLines, setBudgetLines] = useState([]);
    const [geoData, setGeoData] = useState([]);
    const [adminData, setAdminData] = useState([]);
    const [segmentData, setSegmentData] = useState([]);
    const [programmeData, setProgrammeData] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);

    const [loading, setLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [error, setError] = useState("");

    // ================= FILTERS =================
    const [year, setYear] = useState("");
    const [states, setStates] = useState([]);
    const [lgas, setLgas] = useState([]);
    const [selectedStates, setSelectedStates] = useState([]);
    const [selectedLgas, setSelectedLgas] = useState([]);

    const [showStates, setShowStates] = useState(false);
    const [showLgas, setShowLgas] = useState(false);

    const [statesError, setStatesError] = useState(false);
    const [lgasError, setLgasError] = useState(false);

    const [stateSearch, setStateSearch] = useState("");
    const [lgaSearch, setLgaSearch] = useState("");

    // ================= FETCH FILTER DATA =================
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const results = await Promise.allSettled([
                    privateApi.get("/api/state/budgets/states"),
                    privateApi.get("/api/lga/budgets/lgas"),
                ]);

                const [statesRes, lgasRes] = results;

                if (statesRes.status === "fulfilled") {
                    setStates(statesRes.value.data.data || []);
                } else {
                    setStatesError(true);
                    console.error("States failed:", statesRes.reason);
                }

                if (lgasRes.status === "fulfilled") {
                    setLgas(lgasRes.value.data.data || []);
                } else {
                    setLgasError(true);
                    console.error("LGAs failed:", lgasRes.reason);
                }
            } catch (err) {
                setStatesError(true);
                setLgasError(true);
            }
        };

        fetchFilters();
    }, []);

    // ================= FETCH REPORT =================
    useEffect(() => {
        const fetchReport = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams();
                if (year) params.append("year", year);
                if (selectedStates.length > 0) params.append("stateIds", selectedStates.join(","));
                if (selectedLgas.length > 0) params.append("lgaIds", selectedLgas.join(","));
                params.append("limit", 100);
                const { data } = await privateApi.get(`/api/reports?${params.toString()}`);
                const res = data.data;

                setSummary(res.summary || {});
                setBudgetLines(res.budgetLines || []);
                setGeoData(res.geo || []);
                setAdminData(res.admin || []);
                setSegmentData(res.segments || []);
                setProgrammeData(res.programmes || []);
                setExceptions(res.exceptions || []);
                setAuditLogs(res.auditLogs || []);
            } catch (err) {
                setError("Failed to load report");
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [year, selectedStates, selectedLgas]);

    const handleExport = async (type) => {
        try {
            setExportLoading(true);
            const params = new URLSearchParams();

            if (year) params.append("year", year);
            if (selectedStates.length) params.append("stateIds", selectedStates.join(","));
            if (selectedLgas.length) params.append("lgaIds", selectedLgas.join(","));

            // ✅ IMPORTANT: send type (excel or pdf)
            params.append("type", type);

            const res = await privateApi.get(
                `/api/reports/stream-export?${params.toString()}`,
                {
                    responseType: "blob",
                }
            );

            // ✅ Create file download
            const blob = new Blob([res.data], {
                type:
                    type === "pdf"
                        ? "application/pdf"
                        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;

            // ✅ Correct file name
            link.setAttribute(
                "download",
                type === "pdf" ? "report.pdf" : "report.xlsx"
            );

            document.body.appendChild(link);
            link.click();
            link.remove();

            // ✅ Clean up memory
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error(err);
            alert("Export failed");
        } finally {
            setExportLoading(false);
        }
    };

    const resetFilters = () => {
        setYear("");
        setSelectedStates([]);
        setSelectedLgas([]);
    };

    // ================= HANDLERS =================
    const handleStateChange = (id) => {
        setSelectedLgas([]); // Clear LGAs when state changes
        setSelectedStates((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleLgaChange = (id) => {
        setSelectedStates([]); // Clear states when LGA changes
        setSelectedLgas((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

    if (loading) return <Spinner loadingInfo="Loading Budget & Financial Reports..." />

    // ================= UI =================
    return (
        <div className="report">
            <ReportLayout>
                <ReportHeader
                    year={year} setYear={setYear}
                    states={states} selectedStates={selectedStates} handleStateChange={handleStateChange} showStates={showStates} setShowStates={setShowStates} stateSearch={stateSearch} setStateSearch={setStateSearch} statesError={statesError}
                    lgas={lgas} selectedLgas={selectedLgas} handleLgaChange={handleLgaChange} showLgas={showLgas} setShowLgas={setShowLgas} lgaSearch={lgaSearch} setLgaSearch={setLgaSearch} lgasError={lgasError}
                    resetFilters={resetFilters} handleExport={handleExport}
                    years={years} exportLoading={exportLoading}
                />

                {loading ? <p>Loading...</p> : error ? <p className="error">{error}</p> : (
                    <>
                        <SummaryCards summary={summary} />
                        <ChartsSection summary={summary} />
                        <GeographicBreakdown geoData={geoData} />
                        <AdminUnitBreakdown adminData={adminData} />
                        <EconomicSegmentBreakdown segmentData={segmentData} />
                        <ProgrammeBreakdown programmeData={programmeData} />
                        <BudgetLineTable budgetLines={budgetLines} />
                        <ExceptionReports exceptions={exceptions} />
                        <AuditLogs auditLogs={auditLogs} />
                    </>
                )}
            </ReportLayout>
        </div>
    );
};

export default Report;